// Iter44 — Service Requests hub.
//
// Single launcher + cross-module feed listing every service request the
// current tenant has filed: sorting / transport.standard / transport.
// sonderfahrt / rework / engineering / measurement / other. Row click
// deep-links to the originating module workspace; "+ New Service Request"
// opens a type picker which routes to the appropriate creation flow.
//
// Access: any user who holds at least one service module scope (today:
// `sorting` or `tms` — additional modules light up automatically).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { hasModuleAccess } from "../lib/moduleAccess";
import { useStore } from "../store";
import { UniversalFilter } from "../components/filters/UniversalFilter";
import { GenericServiceRequestModal } from "../components/GenericServiceRequestModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABEL = {
  "sorting": "Sorting",
  "transport.standard": "Transport · Standard",
  "transport.sonderfahrt": "Transport · Sonderfahrt",
  "rework": "Rework",
  "engineering": "Engineering",
  "measurement": "Measurement",
  "stock_lock": "Stock Lock",
  "material_movement": "Logistics",
  "other": "Other",
};

const STATUS_COLOR = {
  // common
  requested: "#9ec3ec",
  open: "#9ec3ec",
  in_progress: "#5fa3e3",
  completed: "#4ed084",
  cancelled: "#9aa1b3",
  // tms
  offers_collected: "#c89adb",
  agreed: "#f3b14a",
  dispatched: "#f3b14a",
  in_transit: "#5fa3e3",
  delivered: "#4ed084",
};

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "#f0f2f8",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

export default function ServiceRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const addToast = useStore((s) => s.addToast);
  const [data, setData] = useState({ items: [], counts: {} });
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showPicker, setShowPicker] = useState(false);
  const [genericType, setGenericType] = useState(null);

  const hasAccess = hasModuleAccess(user, "sorting") || hasModuleAccess(user, "tms") || hasModuleAccess(user, "engineering") || hasModuleAccess(user, "stock_lock") || hasModuleAccess(user, "material_movement");

  useEffect(() => {
    if (!hasAccess) return;
    axios.get(`${API}/service-requests/all`).then((r) => setData(r.data))
      .catch((e) => addToast({ type: "error", title: "Failed to load service requests", body: e.message }));
  }, [hasAccess, addToast]);

  const filtered = useMemo(() => {
    return (data.items || []).filter((i) => {
      if (typeFilter !== "all" && i.type !== typeFilter) return false;
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      return true;
    });
  }, [data.items, typeFilter, statusFilter]);

  // Iter51 — universal filter on top of the existing type/status chips.
  const hubChips = useMemo(() => {
    const types = [...new Set((data.items || []).map((i) => i.type))].filter(Boolean);
    const statuses = [...new Set((data.items || []).map((i) => i.status))].filter(Boolean);
    return [
      { key: "type_chip", label: "Type", type: "select", options:
        types.map((tp) => ({ value: tp, label: TYPE_LABEL[tp] || tp, predicate: (i) => i.type === tp })) },
      { key: "status_chip", label: "Status", type: "select", options:
        statuses.map((s) => ({ value: s, label: s.replace(/_/g, " "), predicate: (i) => i.status === s })) },
      { key: "link_chip", label: "Link", type: "select", options: [
        { value: "linked", label: "Linked to incident", predicate: (i) => Boolean(i.incident_id) },
        { value: "standalone", label: "Standalone", predicate: (i) => !i.incident_id },
      ]},
      { key: "date_chip", label: "Created", type: "dateRange", field: "created_at" },
    ];
  }, [data.items]);
  const hubSearchText = useCallback((i) => [
    i.id, i.title, i.incident_id, i.incident_title, i.type, i.status, i.priority,
  ].filter(Boolean).join(" "), []);

  if (!hasAccess) {
    return <div className="p-6" style={mono(11)} data-testid="service-requests-no-access">
      You do not have access to any service module.
    </div>;
  }

  const types = ["all", ...new Set(data.items.map((i) => i.type))];
  const statuses = ["all", ...new Set(data.items.map((i) => i.status))];

  return (
    <div data-testid="service-requests-page">
      <header className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(26px, 3.4vw, 34px)", color: "var(--text, #f0f2f8)", letterSpacing: "-0.5px" }} data-testid="view-title">
            Service Requests
          </h1>
          <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
            All sorting · transport · rework · engineering requests across this tenant
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="px-3 py-2 rounded"
          style={{ background: "#185FA5", color: "#fff", ...mono(11, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
          data-testid="service-requests-new-btn"
        >+ New Service Request</button>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="service-requests-filters">
        <FilterGroup label="Type" value={typeFilter} onChange={setTypeFilter} options={types} counts={data.counts} testIdPrefix="srh-type" />
        <FilterGroup label="Status" value={statusFilter} onChange={setStatusFilter} options={statuses} counts={data.counts} testIdPrefix="srh-status" />
      </div>

      <UniversalFilter
        storageKey="service_requests_hub"
        items={filtered}
        getSearchText={hubSearchText}
        chips={hubChips}
        placeholder="Search id, title, part #, incident, tenant…"
        testIdPrefix="srh-uf"
        emptyMessage="No service requests match the current filter."
      >
        {({ filtered: feed }) => (
          feed.length === 0 ? (
            <div
              className="rounded-md p-8 text-center"
              style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.08)" }}
              data-testid="service-requests-empty"
            >
              <div style={mono(13, "var(--text-dim, #c5cad8)", 600)}>
                No service requests matching the current filter.
              </div>
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                className="mt-3 px-3 py-1.5 rounded"
                style={{ background: "#185FA5", color: "#fff", ...mono(11, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
                data-testid="service-requests-empty-cta"
              >+ New Service Request</button>
            </div>
          ) : (
            <div className="space-y-2" data-testid="service-requests-list">
              {feed.map((it) => (
                <Row key={`${it.kind}-${it.id}`} item={it} onClick={() => {
                  if (it.raw_link) navigate(it.raw_link);
                  else if (it.incident_id) navigate(`/incidents?focus=${encodeURIComponent(it.incident_id)}`);
                }} />
              ))}
            </div>
          )
        )}
      </UniversalFilter>

      {showPicker && (
        <TypePickerModal
          onClose={() => setShowPicker(false)}
          onPick={(opt) => {
            setShowPicker(false);
            if (opt.genericType) {
              setGenericType(opt.genericType);
            } else if (opt.target) {
              navigate(opt.target);
            }
          }}
        />
      )}
      {genericType && (
        <GenericServiceRequestModal
          type={genericType}
          onClose={() => setGenericType(null)}
          onCreated={() => setGenericType(null)}
        />
      )}
    </div>
  );
}

const FilterGroup = ({ label, value, onChange, options, counts, testIdPrefix }) => (
  <div>
    <div className="uppercase mb-1" style={mono(8, "#9198b0", 600, "1.2px")}>{label}</div>
    <div className="flex flex-wrap gap-1">
      {options.map((opt) => {
        const active = value === opt;
        const lbl = opt === "all" ? `All (${counts?.all ?? 0})` : `${TYPE_LABEL[opt] || opt} ${counts?.[opt] != null ? `(${counts[opt]})` : ""}`;
        return (
          <button
            type="button"
            key={opt}
            onClick={() => onChange(opt)}
            className="px-2 py-1 rounded"
            style={{
              background: active ? "rgba(24,95,165,0.18)" : "transparent",
              border: `1px solid ${active ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
              color: active ? "#9ec3ec" : "#c5cad8",
              ...mono(9, undefined, 600, "0.4px"),
              textTransform: "uppercase",
              cursor: "pointer",
            }}
            data-testid={`${testIdPrefix}-${opt}`}
          >{lbl}</button>
        );
      })}
    </div>
  </div>
);

const Row = ({ item, onClick }) => {
  const color = STATUS_COLOR[item.status] || "#9aa1b3";
  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--bg-surface, #0d0f18)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderLeft: `3px solid ${color}`,
        borderRadius: 4,
        padding: "10px 14px",
        cursor: "pointer",
      }}
      data-testid={`service-request-row-${item.id}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <span style={mono(11, "#9ec3ec", 700)}>{item.id}</span>
          <span
            style={{
              background: `${color}28`, color,
              padding: "1px 6px", borderRadius: 2,
              ...mono(9, undefined, 600, "0.5px"), textTransform: "uppercase",
            }}
          >{(item.status || "").replace(/_/g, " ")}</span>
          <span style={mono(9, "#9198b0", 600, "0.5px")} className="uppercase">{TYPE_LABEL[item.type] || item.type}</span>
        </div>
        <span style={mono(9, "#185FA5", 600, "0.4px")}>Open →</span>
      </div>
      <div style={mono(11, "var(--text, #c5cad8)")} className="truncate">{item.title}</div>
      {item.incident_id && (
        <div className="mt-1" style={mono(10, "#9198b0")}>
          ↳ {item.incident_id}{item.incident_title ? ` · ${item.incident_title}` : ""}
        </div>
      )}
      {item.auto_award_skip_reason === "no_contracted_rate" && (
        <div className="mt-1" style={mono(9, "#f3b14a", 600, "0.4px")}>
          No contracted rate — request sent for quotes
        </div>
      )}
    </div>
  );
};

const TypePickerModal = ({ onClose, onPick }) => {
  const { user } = useAuth();
  // Iter79 — split transport into the 2 sub-types + add 5 generic SR types
  // (CMM, 3D scanning, material testing, extra loading/unloading). Generic
  // types open the GenericServiceRequestModal via a `genericType` target.
  const options = [
    hasModuleAccess(user, "tms") && {
      key: "transport_standard",
      title: "Standard Transport",
      desc: "Routine carrier / fleet movement (TMS multi-carrier bidding)",
      target: "/tms?new=1&type=standard",
      testId: "service-requests-type-transport-standard",
    },
    hasModuleAccess(user, "tms") && {
      key: "transport_sonderfahrt",
      title: "Special Transport (Sonderfahrt)",
      desc: "Express / dedicated carrier",
      target: "/tms?new=1&type=sonderfahrt",
      testId: "service-requests-type-transport-sonderfahrt",
    },
    hasModuleAccess(user, "sorting") && {
      key: "sorting",
      title: "Sorting request",
      desc: "Inspection / sort / containment activity",
      target: "/sorting?new=1",
      testId: "service-requests-type-sorting",
    },
    hasModuleAccess(user, "engineering") && {
      key: "engineering",
      title: "Engineering request",
      desc: "Investigation, corrective action, fixture / process change",
      target: "/engineering?new=1",
      testId: "service-requests-type-engineering",
    },
    hasModuleAccess(user, "stock_lock") && {
      key: "stock_lock",
      title: "Stock lock",
      desc: "Quarantine / block / hold material at one or more locations",
      target: "/stock-locks?new=1",
      testId: "service-requests-type-stock-lock",
    },
    (hasModuleAccess(user, "material_movement") || hasModuleAccess(user, "sorting")) && {
      key: "material_movement",
      title: "Logistics request",
      desc: "Move material to / from sorting or between locations",
      target: "/material-movements?new=1",
      testId: "service-requests-type-material-movement",
    },
    // Iter79 — generic SR types (CMM / 3D scan / material testing / extra loading/unloading).
    { key: "cmm_measurement", title: "CMM Measurement", desc: "Coordinate measuring machine inspection",
      genericType: "cmm_measurement", testId: "service-requests-type-cmm-measurement" },
    { key: "scanning_3d", title: "3D Scanning", desc: "Optical / structured-light 3D scan",
      genericType: "3d_scanning", testId: "service-requests-type-scanning-3d" },
    { key: "material_testing", title: "Material Testing", desc: "Lab / mechanical / chemical test",
      genericType: "material_testing", testId: "service-requests-type-material-testing" },
    { key: "extra_loading", title: "Extra Loading", desc: "Additional pickup / loading slot",
      genericType: "extra_loading", testId: "service-requests-type-extra-loading" },
    { key: "extra_unloading", title: "Extra Unloading", desc: "Additional drop-off / unloading slot",
      genericType: "extra_unloading", testId: "service-requests-type-extra-unloading" },
  ].filter(Boolean);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 500,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      data-testid="service-requests-type-picker"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 8, width: "min(520px, 96vw)", padding: 20,
          boxShadow: "0 18px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 18, color: "#f0f2f8" }}>
            New Service Request
          </div>
          <button type="button" onClick={onClose}
            style={{ background: "transparent", border: "none", color: "#9198b0", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={mono(11, "#9198b0")} className="mb-3">Pick the type of request you want to create.</div>
        <div className="space-y-2">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => onPick(o)}
              className="w-full text-left p-3 rounded"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#f0f2f8", cursor: "pointer",
              }}
              data-testid={o.testId}
            >
              <div style={mono(13, "#f0f2f8", 700, "0.4px")} className="uppercase">{o.title}</div>
              <div className="mt-1" style={mono(10, "#9198b0")}>{o.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
